import { DataTable } from "@/components/DataTable/dataTable.component";
import ViewUserDialog from "@/components/UserManagementComponents/Dialogs/DialogViewUser/viewUserDialog.component";
import UserApprovalRejection from "@/components/UserManagementComponents/userApprovalRejection.component";
import { LANGUAGES } from "@/constants/util.constants";
import { fetchUsersList } from "@/models/socket/admin.model";
import { Button } from "@/shadcn/components/ui/button";
import { RoleName, UserType } from "@/types";
import { formatDatePretty } from "@/utils/utils.util";
import { ColumnDef } from "@tanstack/react-table";
import { ArrowUpDown, Eye } from "lucide-react";
import React, { useEffect, useState } from "react";

type UserManagementMainTableProps = {};

const UserManagementMainTable: React.FC<UserManagementMainTableProps> = () => {
  // Using state to allow editing of user data in the table
  const [users, setUsers] = useState<UserType[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [viewingUserID, setViewingUserID] = useState<string | null>(null);
  // Required for EditableTable: tracks which row is being edited

  async function fetchUsers() {
    setLoading(true);
    setError(null);

    const response = await fetchUsersList();
    if (response.rStatus === "success") {
      setUsers(response.rData);
    } else {
      setError(response.rData);
    }
    setLoading(false);
  }

  useEffect(() => {
    fetchUsers();
  }, []);

  // Handler for updating a user's role
  function handleRoleChange(
    userId: string,
    roleAction: "approve" | "block" | "admin"
  ) {
    const newRole: RoleName =
      roleAction === "approve"
        ? "approved_user"
        : roleAction === "block"
          ? "blocked_user"
          : "admin";

    // Using state update to trigger re-render after role change
    setUsers((prev) =>
      prev.map((user) =>
        user.id === userId ? { ...user, roleName: newRole } : user
      )
    );
  }

  function updateUser() {
    setViewingUserID(null);
    fetchUsers();
  }

  // Defining columns for the users table
  const columns: ColumnDef<UserType>[] = [
    {
      header: "View",
      accessorKey: "view",
      cell: ({ row }) => {
        // Add view action at the end for user convenience
        return (
          <div className="flex items-center justify-center">
            <Eye
              className="m-auto size-5 cursor-pointer self-center"
              onClick={() => setViewingUserID(row.original.id)}
            />
          </div>
        );
      },
      size: 40,
      enableSorting: false,
      enableHiding: false,
    },
    {
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="!bg-transparent !p-0"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          ID
          <ArrowUpDown />
        </Button>
      ),
      accessorKey: "id",
      enableSorting: true,
      enableHiding: false,
      size: 100,
      cell: ({ cell }) => <span>{String(cell.getValue() || "N/A")}</span>,
    },
    {
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="!bg-transparent !p-0"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Name
          <ArrowUpDown />
        </Button>
      ),
      accessorKey: "fullName",
      enableSorting: true,
      enableHiding: false,
      size: 220,
      cell: ({ cell }) => <span>{String(cell.getValue() || "N/A")}</span>,
    },
    {
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="!bg-transparent !p-0"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Email
          <ArrowUpDown />
        </Button>
      ),
      accessorKey: "email",
      enableSorting: true,
      enableHiding: false,
      size: 240,
      cell: ({ cell }) => <span>{String(cell.getValue() || "N/A")}</span>,
    },
    {
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="!bg-transparent !p-0"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Phone
          <ArrowUpDown />
        </Button>
      ),
      accessorKey: "phoneNumber",
      enableSorting: true,
      enableHiding: false,
      size: 150,
      cell: ({ cell }) => <span>{String(cell.getValue() || "N/A")}</span>,
    },
    {
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="!bg-transparent !p-0"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Designation
          <ArrowUpDown />
        </Button>
      ),
      accessorKey: "designation",
      enableSorting: true,
      enableHiding: false,
      size: 150,
      cell: ({ cell }) => <span>{String(cell.getValue() || "N/A")}</span>,
    },
    {
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="!bg-transparent !p-0"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Role
          <ArrowUpDown />
        </Button>
      ),
      accessorKey: "roleName",
      enableSorting: true,
      enableHiding: false,
      size: 110,
      // Using a select input to allow editing of the user's role
      cell: ({ row }) => {
        const user = row.original;
        const niceRoleName =
          user.roleName === "super_admin"
            ? "Super Admin"
            : user.roleName === "admin"
              ? "Admin"
              : user.roleName === "approved_user"
                ? "Approved User"
                : user.roleName === "blocked_user"
                  ? "Blocked User"
                  : "New User";
        return niceRoleName;
      },
      meta: {
        enableEditing: true,
      },
    },
    {
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="!bg-transparent !p-0"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Language
          <ArrowUpDown />
        </Button>
      ),
      accessorKey: "preferredLanguage",
      enableSorting: true,
      enableHiding: false,
      size: 180,
      cell: ({ cell }) => {
        const language = cell.getValue() as string | null;
        // Null check is required here to avoid calling toLowerCase() on null, which would cause a runtime error.
        const foundLanguage = language
          ? LANGUAGES.find(
              (lang) => lang.code.toLowerCase() === language.toLowerCase()
            )
          : undefined;
        return <span>{foundLanguage ? foundLanguage.name : "N/A"}</span>;
      },
    },
    {
      // preferredCommunicationMethods
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="!bg-transparent !p-0"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Communication Methods
          <ArrowUpDown />
        </Button>
      ),
      accessorKey: "preferredCommunicationMethods",
      enableSorting: true,
      enableHiding: false,
      size: 200,
      cell: ({ cell }) => {
        const methods = cell.getValue() as
          | UserType["preferredCommunicationMethods"]
          | null;
        // Using a mapping to ensure display is user-friendly and consistent with UI expectations
        const methodLabels: Record<string, string> = {
          email: "Email",
          sms: "SMS",
          // push_notification: "Push Notifications",
        };
        if (!Array.isArray(methods)) {
          return <span>N/A</span>;
        }
        return (
          <span>
            {methods && methods.length > 0
              ? methods.map((m) => methodLabels[m] || m).join(", ")
              : "N/A"}
          </span>
        );
      },
    },
    {
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="!bg-transparent !p-0"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Marketing Opt-In
          <ArrowUpDown />
        </Button>
      ),
      accessorKey: "marketingOptIn",
      enableSorting: true,
      enableHiding: false,
      size: 150,
      cell: ({ cell }) => {
        const optIn = cell.getValue() as boolean | null;
        return <span>{optIn ? "Yes" : "No"}</span>;
      },
    },
    {
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="!bg-transparent !p-0"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Last Login
          <ArrowUpDown />
        </Button>
      ),
      accessorKey: "lastLogin",
      enableSorting: true,
      enableHiding: false,
      size: 140,
      cell: ({ cell }) => {
        const lastLogin = cell.getValue() as string | null;
        // Format the last login date if available
        if (!lastLogin) {
          return <span>N/A</span>;
        }
        const date = formatDatePretty(lastLogin, true, true);
        return <span>{date ? date : "N/A"}</span>;
      },
    },
    // {
    //   header: "Note",
    //   accessorKey: "note",
    //   enableSorting: true,
    //   enableHiding: false,
    //   // Show N/A for null/empty notes for consistency
    //   cell: ({ cell }) =>
    //     cell.getValue() ? (
    //       <HoverCard openDelay={200} closeDelay={200}>
    //         <HoverCardTrigger asChild>
    //           <Button variant="ghost">
    //             <Notebook className="h-5 w-5" />
    //           </Button>
    //         </HoverCardTrigger>
    //         <HoverCardContent
    //           className="max-h-80 max-w-50 overflow-y-auto rounded-md bg-neutral-900 p-1 text-sm whitespace-break-spaces"
    //           style={{
    //             scrollbarWidth: "none",
    //           }}
    //         >
    //           <div className="flex justify-between gap-4">
    //             <span className="text-sm">
    //               {String(cell.getValue() || "N/A")}
    //             </span>
    //           </div>
    //         </HoverCardContent>
    //       </HoverCard>
    //     ) : (
    //       <></>
    //     ),
    // },
    {
      header: () => (
        <Button variant="ghost" className="!bg-transparent !p-0">
          Actions
        </Button>
      ),
      accessorKey: "action",
      enableSorting: false,
      enableHiding: false,
      size: 80,
      cell: ({ row }) => {
        const user = row.original;

        return (
          <UserApprovalRejection
            userFullName={user.fullName}
            userID={user.id}
            userRoleName={user.roleName}
            updateRole={handleRoleChange}
          />
        );
      },
    },
  ];

  return loading ? (
    <div className="py-4">
      <div className="flex items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-b-3 border-white"></div>
      </div>
      <div className="flex items-center justify-center">
        <h2 className="mt-2 text-xl text-white">Loading Users...</h2>
      </div>
    </div>
  ) : error ? (
    <div className="mt-4 text-center text-red-500">
      <p>Error fetching users: {error}</p>
    </div>
  ) : (
    <div className="mx-4">
      <DataTable
        columns={columns}
        data={users}
        initialState={{
          sorting: [
            {
              id: "id",
              desc: false,
            },
          ],
        }}
        includeSelectRow={false}
        disableColumnSelection={true}
      />
      {viewingUserID && (
        <ViewUserDialog
          userID={viewingUserID}
          onClose={() => setViewingUserID(null)}
          updateUser={updateUser}
        />
      )}
    </div>
  );
};

export default UserManagementMainTable;
